import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import { useSelector } from 'react-redux';
import { get } from 'lodash-es';
import { Image } from 'site-modules/shared/components/image/image';
import { SOURCES } from 'client/utils/image-helpers';
import { Link } from 'site-modules/shared/components/link/link';
import { buildAppraisalHistoryUrl } from 'site-modules/shared/utils/appraisal/appraisal-url-builders';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';

import './tcv-page-cta.scss';

const CREATIVE_ID = 'edm-entry-track-car-value-module';

export function TcvPageCta({
  getVehicleData,
  zipCode = '',
  condition = '',
  mileage = '',
  optionIds = [],
  modsId = '',
  className = '',
  fireWidgetView = false,
  vin = '',
}) {
  useEffect(() => {
    if (fireWidgetView) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
        event_data: {
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
          creative_id: CREATIVE_ID,
        },
      });
    }
  }, [fireWidgetView]);

  const isAppExtensionPage = useSelector(state => get(state, 'pageContext.isAppExtensionPage'));
  const { makeName, makeSlug, modelName, modelSlug, year, styleId } = getVehicleData();
  const appraisalHistoryUrl = buildAppraisalHistoryUrl({
    vin,
    makeSlug,
    modelSlug,
    year,
    condition,
    styleId,
    zipCode,
    mileage,
    optionIds,
    modsId,
    isAppExtensionPage,
  });

  const ctaText = 'See pricing history';

  const getButton = ctaClassName => (
    <Button
      tag={Link}
      target="_blank"
      rel="noopener noreferrer"
      to={appraisalHistoryUrl}
      color="success"
      className={classnames('text-transform-none size-16', ctaClassName)}
      data-tracking-id="track_car_value_cta_click"
    >
      {ctaText}
    </Button>
  );

  return (
    <div data-tracking-parent={CREATIVE_ID} className={classnames('tcv-page-cta', className)}>
      <div className="mobile-logo-wrapper text-center mb-1 d-md-none">
        <div className="d-inline-block bg-white px-0_5">
          <Image
            imageId="appraisal/tcv-page-cta/mobile-graph.svg"
            alt=""
            source={SOURCES.STATIC}
            lazy
            height={48}
            width={48}
          />
        </div>
      </div>
      <div className="d-flex align-items-end align-items-md-center mb-1_75 mb-md-0">
        <div className="pe-lg-3 pe-md-1_75">
          <div className="text-center text-md-start">
            <h2
              className="cta-label text-gray-darkest text-uppercase size-12 fw-bold mb-0_5"
              aria-label="See Edmunds Historical Pricing Data"
            >
              See Edmunds pricing data
            </h2>
            <h3 className="heading-3 mb-1">
              Is Now The{' '}
              <span className="text-blue-50">
                Right <br className="d-md-none" />
                Time To Sell?
              </span>
            </h3>
          </div>
          <p className="size-16 text-gray-darker mb-0 mb-md-1_75">
            <span className="d-none d-md-inline">Used car values are constantly changing.</span> Edmunds lets you track
            your{' '}
            <strong className="fw-bold">
              {year} {makeName} {modelName}&apos;s
            </strong>{' '}
            value over time so you can decide when to sell or trade in.
          </p>
          <div className="d-none d-md-block">{getButton()}</div>
        </div>
        <Image
          imageId={'appraisal/tcv-page-cta/desktop-graph.svg'}
          alt=""
          source={SOURCES.STATIC}
          lazy
          width={240}
          height={237}
          className="d-none d-md-block"
        />
      </div>
      <div className="d-md-none">{getButton('d-block w-100')}</div>
    </div>
  );
}

TcvPageCta.propTypes = {
  getVehicleData: PropTypes.func.isRequired,
  zipCode: PropTypes.string,
  condition: PropTypes.string,
  mileage: PropTypes.string,
  optionIds: PropTypes.arrayOf(PropTypes.string),
  modsId: PropTypes.string,
  className: PropTypes.string,
  fireWidgetView: PropTypes.bool,
  vin: PropTypes.string,
};
